import { useState, useEffect, useCallback, useRef } from "react";
import { useAnimations, Html } from "@react-three/drei";
import { useLoader, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import "./Object.scss";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { useTranslation } from "react-i18next";

interface ObjectProps {
  distanceFactor: number;
  questionsData: any;
  sceneName: string;
  selectedQuestion: any;
  setSelectedQuestion: (question: any) => void;
  objectPath: string;
  quizCompletions: { [key: string]: boolean };
  setIsQuizMode: (value: boolean) => void;
  setModalContent: (content: any) => void;
  objectIntroPath?: string;
  objectIntroEndTime?: number;
}

export function Objects({
  distanceFactor,
  questionsData,
  sceneName,
  selectedQuestion,
  setSelectedQuestion,
  objectPath,
  setIsQuizMode,
  setModalContent,
  objectIntroPath,
  objectIntroEndTime,
}: ObjectProps) {
  const gltfObj = useLoader(GLTFLoader, objectPath);
  const gltfBinderPos = useLoader(
    GLTFLoader,
    "assets/models/objects/tribunal_hearing_room_object-positions.gltf",
  );
  const { nodes: binderNodes } = gltfBinderPos;

  const [gltfAnimation, setGltfAnimation] = useState<any>();

  // Intro animation state
  const [isObjectIntroPlaying, setIsObjectIntroPlaying] =
    useState(!!objectIntroPath);
  const isObjectIntroPlayingRef = useRef(!!objectIntroPath);
  const [introGltfAnimation, setIntroGltfAnimation] = useState<any>(null);
  const introActionRef = useRef<THREE.AnimationAction | null>(null);

  const { nodes, animations } = gltfObj;
  const { actions: idleActions } = useAnimations(animations, gltfObj.scene);
  const { actions: selectedActions } = useAnimations(
    gltfAnimation ? gltfAnimation.animations : [],
    gltfObj.scene,
  );
  const { actions: introActions } = useAnimations(
    introGltfAnimation ? introGltfAnimation.animations : [],
    gltfObj.scene,
  );

  // HTML labels are hidden while a question is selected OR while the object intro is playing
  const htmlVisible = !selectedQuestion && !isObjectIntroPlaying;
  const prevHtmlVisible = useRef(htmlVisible);
  if (prevHtmlVisible.current !== htmlVisible) {
    prevHtmlVisible.current = htmlVisible;
  }

  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState<
    number | null
  >(null);
  const clickedObjectsRef = useRef<{ [key: string]: number[] }>({});

  const clickedStorageKey = "spirit-bear-clicked-objects-by-scene";

  const { i18n: i18nInstance } = useTranslation();
  const lang = i18nInstance.language;

  // Load the selected animation, if any
  useEffect(() => {
    if (selectedQuestion && selectedQuestion.objAnimation) {
      const loader = new GLTFLoader();
      loader.load(
        selectedQuestion.objAnimation,
        setGltfAnimation,
        undefined,
        (error) => {
          console.warn(
            `[Scene] Missing object animation: ${selectedQuestion.objAnimation}`,
            error,
          );
          setGltfAnimation(null);
        },
      );
    } else {
      setGltfAnimation(null);
    }
  }, [selectedQuestion]);

  // Handle animations
  useEffect(() => {
    let idleAction = Object.values(idleActions)[0] as THREE.AnimationAction;
    let selectedAction = gltfAnimation
      ? (Object.values(selectedActions)[0] as THREE.AnimationAction)
      : null;
    let mixer: THREE.AnimationMixer | null = null;
    let intervalId: NodeJS.Timeout | null = null;
    const intervalDuration: Record<string, number> = {
      LakeBearbine: 10000,
      HeartGarden: 11000,
      HearingRoom: 11330,
      ParliamentHill: 8600,
      Classroom: 10000,
    };

    // Helper function to clear existing interval
    const clearExistingInterval = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const resetToIdle = () => {
      // If the object intro is still playing, don't restart idle yet
      if (isObjectIntroPlayingRef.current) {
        return;
      }
      if (selectedAction) selectedAction.stop();
      if (idleAction) {
        // Only reset+play if idle is not already running (avoids snapping mid-cycle)
        if (!idleAction.isRunning()) {
          idleAction.reset().play();
        }
        clearExistingInterval();
        intervalId = setInterval(() => {
          idleAction.stop().play();
        }, intervalDuration[sceneName]);
      }
    };

    if (selectedQuestion && selectedAction) {
      if (idleAction) idleAction.stop();
      selectedAction.reset().play();
      mixer = new THREE.AnimationMixer(selectedAction.getRoot());
      mixer.addEventListener("finished", () => {
        selectedAction.stop();
        resetToIdle();
      });
    } else if (!selectedQuestion) {
      // Only reset idle when a question is being cleared, not when one is
      // selected but has no objAnimation (e.g. LakeBearbine) — in that case
      // the idle animation should keep playing undisturbed.
      resetToIdle();
    }

    // Cleanup function
    return () => {
      clearExistingInterval();
      if (mixer) {
        mixer.removeEventListener("finished", resetToIdle);
        mixer.stopAllAction();
      }
    };
  }, [selectedQuestion, idleActions, gltfAnimation, selectedActions]);

  // Hide objects until the intro animation is ready — prevents a flash of the rest pose
  useEffect(() => {
    if (objectIntroPath) {
      gltfObj.scene.visible = false;
    }
  }, []);

  // Load objectIntro animation when objectIntroPath is provided
  useEffect(() => {
    if (!objectIntroPath) {
      return;
    }
    let cancelled = false;
    const loader = new GLTFLoader();
    loader.load(
      objectIntroPath,
      (gltf) => {
        if (cancelled) return;
        setIntroGltfAnimation(gltf);
      },
      undefined,
      (error) => {
        if (cancelled) return;
        gltfObj.scene.visible = true;
        isObjectIntroPlayingRef.current = false;
        setIsObjectIntroPlaying(false);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [objectIntroPath]);

  // Play objectIntro animation once it's loaded
  useEffect(() => {
    if (
      !introGltfAnimation ||
      !introActions ||
      Object.keys(introActions).length === 0
    ) {
      return;
    }
    const introAction = Object.values(
      introActions,
    )[0] as THREE.AnimationAction | null;
    if (!introAction) {
      gltfObj.scene.visible = true;
      isObjectIntroPlayingRef.current = false;
      setIsObjectIntroPlaying(false);
      return;
    }
    const idleAction = Object.values(idleActions)[0] as
      | THREE.AnimationAction
      | undefined;

    if (idleAction) idleAction.stop();
    gltfObj.scene.visible = true;

    introAction.setLoop(THREE.LoopOnce, 1);
    introAction.clampWhenFinished = true;
    introAction.reset().play();
    introActionRef.current = introAction;
  }, [introGltfAnimation, introActions, idleActions]);

  // Poll the intro action's time each frame — reveal labels the instant the clip ends
  const lastLoggedSecond = useRef(-1);
  useFrame(() => {
    const introAction = introActionRef.current;
    if (!introAction || !isObjectIntroPlayingRef.current) return;
    const duration = introAction.getClip().duration;
    const cutoff = objectIntroEndTime ?? duration;
    const currentSecond = Math.floor(introAction.time);
    if (currentSecond !== lastLoggedSecond.current) {
      lastLoggedSecond.current = currentSecond;
    }
    if (introAction.time >= cutoff) {
      introAction.stop();
      introActionRef.current = null;
      isObjectIntroPlayingRef.current = false;
      setIsObjectIntroPlaying(false);
      const idleAction = Object.values(idleActions)[0] as
        | THREE.AnimationAction
        | undefined;
      if (idleAction) {
        idleAction.reset().play();
      }
    }
  });

  const handleDown = (index: number) => {
    if (sceneName !== "Classroom") {
      setSelectedQuestionIndex(index);
    }
  };

  const handleUp = (index: number) => {
    if (sceneName !== "Classroom") {
      setSelectedQuestionIndex(null);
      setSelectedQuestion(questionsData[index]);
    }
  };

  // Generate Html elements
  const elements = useCallback(() => {
    const elmNodes = sceneName === "HearingRoom" ? binderNodes : nodes;
    return Object.keys(elmNodes)
      .filter((node) => node.includes("Object"))
      .map((node, index) => (
        <group
          key={node + sceneName}
          position={[
            elmNodes[node].position.x,
            elmNodes[node].position.y,
            elmNodes[node].position.z,
          ]}
        >
          {htmlVisible && (
            <Html
              center
              distanceFactor={distanceFactor}
              key={`html-${node}`}
              zIndexRange={[10, 0]}
            >
              <div
                onMouseDown={() => handleDown(index)}
                onMouseUp={() => handleUp(index)}
                onTouchStart={() => handleDown(index)}
                onTouchEnd={() => handleUp(index)}
                className={`Questions ${sceneName} ${lang} idx-${index}`}
              >
                <p
                  style={{
                    fontSize:
                      index === selectedQuestionIndex ? "1.25vw" : "1.15vw",
                  }}
                >
                  {lang === "fr"
                    ? questionsData[index].questionText_fr
                    : questionsData[index].questionText_en}
                </p>
              </div>
            </Html>
          )}
        </group>
      ));
  }, [
    nodes,
    sceneName,
    htmlVisible,
    questionsData,
    binderNodes,
    selectedQuestionIndex,
    lang,
  ]);

  return (
    <>
      <primitive object={gltfObj.scene} />
      {elements()}
    </>
  );
}
